import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { AddModal } from "./add-modal";
import SelectField from "@/components/common/select";
import RichTextEditor from "@/components/common/editor";
import { editTypesEnum } from "@/types/entities/legal";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useLegalStore } from "@/stores/legal/useLegalStore";

interface SectionOption {
  label: string;
  value: string;
  showIndicator: boolean;
}

interface FaqModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: {
    question: string;
    category: string;
    answer: string;
  }) => void;
  canDelete: boolean;
  onDelete?: () => void;
  sectionList: SectionOption[];

  isEditing?: boolean;
  initialQuestion?: string;
  initialCategory?: string;
  initialAnswer?: string;
}

// Utility to strip HTML and check if there's any text content
const getTextContent = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const FaqModal: FC<FaqModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  canDelete,
  sectionList,
  isEditing = false,
  initialQuestion = "",
  initialCategory = "",
  initialAnswer = "",
}) => {
  const [question, setQuestion] = useState(initialQuestion);
  const [category, setCategory] = useState(initialCategory);
  const [answer, setAnswer] = useState(initialAnswer);

  // Importamos el store del editor
  const { setSelected } = useLegalStore();

  useEffect(() => {
    if (isOpen) {
      setQuestion(initialQuestion);
      setCategory(initialCategory);
      setAnswer(initialAnswer);

      // Leemos el selected *actual* con getState()
      const current = useLegalStore.getState().selected;

      // Y asignamos el nuevo objeto tal cual lo definié tu store
      setSelected({
        ...current,
        content: initialAnswer,
      });
    }
  }, [isOpen, initialQuestion, initialCategory, initialAnswer, setSelected]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.({ question, category, answer });
  };

  // Validaciones: aseguramos que todos los campos estén llenos y la respuesta no exceda el límite
  const strippedAnswer = getTextContent(answer);
  const isAnswerEmpty = strippedAnswer === "";
  const isAnswerTooLong = answer.length >= 1500;
  const isFormValid =
    question.trim() !== "" &&
    category.trim() !== "" &&
    !isAnswerEmpty &&
    !isAnswerTooLong;

  return (
    <AddModal
      onClose={onClose}
      isOpen={isOpen}
      header={
        <div className="flex items-center justify-between px-8 py-3 border-b border-neutral-silent">
          <Heading variant="h5" className="!mb-0">
            {isEditing ? "Editar Pregunta" : "Nueva Pregunta"}
          </Heading>
          <Button
            type="button"
            variant="quiet"
            aria-label="Cerrar"
            onClick={onClose}
            className="w-8 h-8"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full flex flex-col md:flex-row border-t border-neutral-silent py-3 px-8 gap-3">
          {canDelete && onDelete && (
            <Button
              variant="danger-tertiary"
              size="sm"
              type="button"
              onClick={onDelete}
              className="w-full md:max-w-[120px]"
            >
              Eliminar
            </Button>
          )}
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={onClose}
            className="w-full md:max-w-[120px] md:ml-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            size="sm"
            type="submit"
            disabled={!isFormValid}
            form="faq-form"
            className="w-full md:max-w-[140px]"
          >
            {isEditing ? "Guardar cambios" : "Crear pregunta"}
          </Button>
        </div>
      }
    >
      <form
        id="faq-form"
        data-testid="faq-form"
        onSubmit={handleSubmit}
        className="px-8 space-y-4"
      >
        <div>
          <label
            htmlFor="question"
            className="font-semibold text-body-loud text-sm leading-2xl"
          >
            Pregunta <span className="text-sm text-danger">*</span>
          </label>
          <input
            maxLength={120}
            id="question"
            name="question"
            type="text"
            placeholder="Escribe una pregunta"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full border border-neutral-quiet rounded p-2 text-body-loud bg-neutral-object-accent-silent"
            required
          />
        </div>

        <div>
          <SelectField
            showIndicator
            name="section"
            label="Sección"
            placeholder="Elige una sección"
            value={category}
            options={sectionList}
            onChange={(value) => setCategory(value)}
            required
          />
        </div>

        <div>
          <label className="font-semibold text-body-loud text-sm leading-2xl">
            Respuesta <span className="text-sm text-danger">*</span>
          </label>
          <div className="border border-neutral-quiet overflow-hidden rounded-xl shadow">
            <RichTextEditor
              basic
              editionType={editTypesEnum.visual}
              value={(html: string) => setAnswer(html)}
            />
          </div>
          {answer.length >= 1500 && (
            <p className="mt-1 text-sm text-danger pt-1">
              La respuesta es demasiado extensa
            </p>
          )}
        </div>
      </form>
    </AddModal>
  );
};

export default FaqModal;
